import squad from "@/assets/appLanding/preview-squad.webp";
import upgradeCard from "@/assets/appLanding/upgrade-card.webp";
import { cn } from "@/lib/utils";
import { useTranslation } from "@repo/shared/i18n";
import { ArrowLeftRight, Check } from "lucide-react";

// Brand name and a sample value: the same in every language.
const CLUB_NAME = "EasySBC FC";
const CLUB_VALUE = "751K";
const COIN = `${import.meta.env.VITE_APP_ASSETS_BASE_URL}/misc/fut-coin-large.png`;

/*
  The pitch crop and where Manzambi (card and rating chip) sits in it, in
  percent. A new crop must update both together.
*/
const SQUAD = {
  width: 1001,
  height: 1078,
  spotlight: { left: "63.5%", top: "40%", width: "23%", height: "19.5%" },
};

const TOP_CARD = "relative shadow-[0_18px_40px_rgba(0,0,0,.55)]";

/* Behind the upgrade card: the next recommendations, receding. */
const STACK = [
  "translate-y-6 scale-x-[.86] bg-[#1d2236]",
  "translate-y-3 scale-x-[.93] bg-[#252b45]",
];

/** The hero phone: My Club as the app shows it, staged rather than screenshotted. */
export const PhoneShowcase = () => {
  const { t } = useTranslation(["appLanding", "myClub"]);

  const card = (className?: string) => (
    <img
      src={upgradeCard}
      alt=""
      width={826}
      height={474}
      className={cn("w-full rounded-xl", className)}
    />
  );

  return (
    <div aria-hidden className="flex flex-1 justify-center select-none">
      <div className="relative isolate w-[300px] max-w-full mobile:w-[340px] touch-device:w-[360px]">
        <div className="absolute inset-x-[-20%] top-1/4 -z-10 h-1/2 rounded-full bg-primary-action/25 blur-3xl" />

        <div className="aspect-[9/18] w-full rounded-[48px] border border-neutral-700 bg-[#0a0a0a] p-3 shadow-[0_30px_80px_rgba(23,37,84,.55)]">
          <div className="relative flex size-full flex-col gap-4 overflow-hidden rounded-[36px] bg-neutral-900 px-2 pt-11">
            <div className="absolute left-1/2 top-3 h-6 w-24 -translate-x-1/2 rounded-full bg-[#0a0a0a]" />

            <div className="flex items-center justify-between gap-2 px-1">
              <div className="flex min-w-0 items-center gap-2">
                <img src="/logo.png" alt="" className="size-7 shrink-0" />
                <div className="flex min-w-0 flex-col">
                  <span className="truncate font-semibold leading-tight text-neutral-50">
                    {CLUB_NAME}
                  </span>
                  <span className="flex items-center gap-1 text-xs text-sbc-club">
                    <Check className="size-3" strokeWidth={3} />
                    {t("hero.clubImported")}
                  </span>
                </div>
              </div>
              <div className="flex flex-col items-end">
                <span className="text-xs text-neutral-400">
                  {t("myClub:bar.clubValue")}
                </span>
                <span className="flex items-center gap-1 font-oswald text-sm font-semibold text-neutral-50">
                  <img src={COIN} alt="" className="size-3.5" />
                  {CLUB_VALUE}
                </span>
              </div>
            </div>

            <div className="-mt-1 flex items-center gap-2 px-1 text-sm font-semibold text-neutral-50">
              <ArrowLeftRight className="size-4 shrink-0 text-sbc-club" />
              {t("hero.swap", { from: "Ederson", to: "Manzambi" })}
            </div>

            <div className="relative -mx-2 -mt-2 overflow-hidden">
              <img
                src={squad}
                alt=""
                width={SQUAD.width}
                height={SQUAD.height}
                className="w-full"
              />
              {/* Dims the pitch around the upgraded player. */}
              <div
                style={SQUAD.spotlight}
                className="absolute rounded-2xl shadow-[0_0_0_9999px_rgba(0,0,0,.3)]"
              />
            </div>

            <div className="relative">
              {STACK.map((className) => (
                <div
                  key={className}
                  className={cn(
                    "absolute inset-0 rounded-xl border border-white/10",
                    className,
                  )}
                />
              ))}
              {card(TOP_CARD)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
